import { dateToTimestamp } from '@/utils'
import { clampComboProdQty } from '@/utils/product'
import { resolvePromotionDetailProductImage } from './activityProductImage'
import { PROMOTION_TYPE } from './promotionTypes'
import {
  mapBuyGiftTiersToRuleGroups,
  buildDetailBuyGiftPromoTags,
  sortBuyGiftTiers,
} from './buyGiftPromotion'
import {
  getLowestPromotionTier,
  pickPromotionCatalogUnitPrice,
  resolveActivityCountdown,
} from './promotionDisplay'

/**
 * @description 归一化活动详情字段（列表/详情统一 activity* 命名，兼容旧 promotion* / displayStatus）
 * @param {Object|null|undefined} detail
 * @returns {Object|null}
 */
export function normalizePromotionDetailFields(detail) {
  if (!detail || typeof detail !== 'object') return null

  const activityId = detail.activityId ?? detail.promotionId
  const activityCode = detail.activityCode ?? detail.promotionCode
  const activityName = detail.activityName ?? detail.promotionName
  const activityType = detail.activityType ?? detail.promotionType
  const activityStatus = detail.activityStatus ?? detail.displayStatus
  const activityStatusText = detail.activityStatusText ?? detail.displayStatusText

  return {
    ...detail,
    activityId,
    activityCode,
    activityName,
    activityType,
    activityStatus,
    activityStatusText,
    promotionId: activityId,
    promotionCode: activityCode,
    promotionName: activityName,
    promotionType: activityType,
    displayStatus: activityStatus,
    displayStatusText: activityStatusText,
  }
}

/**
 * @description 解析活动详情接口响应
 * @param {unknown} res
 * @returns {Object|null}
 */
export function unwrapPromotionDetail(res) {
  if (!res || typeof res !== 'object') return null
  if (
    res.activityId != null ||
    res.activityType != null ||
    res.promotionId != null ||
    res.promotionType != null
  ) {
    return normalizePromotionDetailFields(res)
  }
  if (res.data === null) return null
  if (res.data && typeof res.data === 'object') return unwrapPromotionDetail(res.data)
  return null
}

/**
 * @description 归一化商品 ID（兼容 prodId / id、字符串/数字）
 * @param {unknown} value
 * @returns {number|null}
 */
export function normalizeStoreProdId(value) {
  const num = Number(value)
  return Number.isFinite(num) && num > 0 ? num : null
}

/**
 * @description 判断两个商品 ID 是否指向同一商品
 * @param {unknown} left
 * @param {unknown} right
 * @returns {boolean}
 */
export function isSameStoreProdId(left, right) {
  const a = normalizeStoreProdId(left)
  const b = normalizeStoreProdId(right)
  return a !== null && b !== null && a === b
}

/**
 * @description 组合促销阶梯商品展开为抽屉 adjustProds 行（读 tiers[].products[].skus）
 * @param {Object|null|undefined} combinationPromotion
 * @param {{ tier?: Object|null }} [options]
 * @returns {Array<Object>}
 */
export function flattenCombinationPromotionAdjustProds(combinationPromotion, options = {}) {
  const tiers = Array.isArray(combinationPromotion?.tiers) ? combinationPromotion.tiers : []
  const tier = options.tier ?? getLowestPromotionTier(tiers)
  if (!tier) return []

  const products = Array.isArray(tier.products) ? tier.products : []
  const rows = []

  for (const product of products) {
    const skuRows =
      Array.isArray(product.skus) && product.skus.length > 0
        ? product.skus
        : product.skuId != null
          ? [
              {
                skuId: product.skuId,
                specText: product.specText,
                skuImage: product.prodImage,
                promotionPrice: product.promotionPrice,
                originalPrice: product.unitPrice,
                startNum: product.minNum,
              },
            ]
          : []

    for (const sku of skuRows) {
      const qtyMeta = {
        minNum: sku.minNum ?? product.minNum,
        maxNum: sku.maxNum ?? product.maxNum,
        leastNum: sku.startNum ?? sku.minNum ?? product.minNum,
        startNum: sku.startNum ?? product.minNum,
      }
      const quantity = clampComboProdQty(qtyMeta, sku.startNum ?? product.minNum ?? 1)

      rows.push({
        prodId: product.prodId,
        skuId: sku.skuId,
        prodName: product.prodName,
        name: product.prodName,
        pic: product.prodImage || sku.skuImage,
        image: product.prodImage || sku.skuImage,
        price: Number(sku.promotionPrice ?? product.promotionPrice ?? 0),
        promotionPrice: Number(sku.promotionPrice ?? product.promotionPrice ?? 0),
        originalPrice: Number(
          pickPromotionCatalogUnitPrice(sku) ?? pickPromotionCatalogUnitPrice(product) ?? 0
        ),
        unitPrice: Number(
          pickPromotionCatalogUnitPrice(product) ?? pickPromotionCatalogUnitPrice(sku) ?? 0
        ),
        spec: sku.specText || product.specText || '',
        prodUnit: product.prodUnit ?? sku.prodUnit ?? '',
        boxGauge: sku.boxGauge ?? product.boxGauge ?? sku.box_gauge ?? product.box_gauge,
        boxSpec: sku.boxSpec ?? product.boxSpec ?? sku.packageSpec ?? product.packageSpec,
        ...qtyMeta,
        quantity,
        prodCount: quantity,
        required: product.required,
      })
    }
  }

  return rows
}

/**
 * @description 组合促销详情映射为抽屉 group（读 combinationPromotion.tiers）
 * @param {Object|null|undefined} detail
 * @param {Object} [card]
 * @returns {Object|null}
 */
export function mapCombinationPromotionDetailToDrawerGroup(detail, card = {}) {
  const normalized = normalizePromotionDetailFields(detail)
  if (!normalized) return null

  const combinationPromotion = normalized.combinationPromotion || {}
  const tiers = Array.isArray(combinationPromotion.tiers) ? combinationPromotion.tiers : []
  const defaultTier = getLowestPromotionTier(tiers)
  const adjustProds = flattenCombinationPromotionAdjustProds(combinationPromotion, {
    tier: defaultTier,
  })

  const activityStatus = normalized.activityStatus
  const isNotStarted = activityStatus === 'NOT_STARTED'
  const startTime = normalized.startTime ?? normalized.activityStartTime ?? card.activityStartTime
  const endTime = normalized.endTime ?? normalized.activityEndTime ?? card.activityEndTime
  const countdownLabel = isNotStarted ? '距开始' : '仅剩'
  const targetTime = isNotStarted ? startTime : endTime
  const countdown = resolveActivityCountdown({
    startTime,
    endTime,
    activityStatus,
  })
  const pendingActionText = isNotStarted && countdown.countdownText ? countdown.countdownText : ''

  const comboTotalPrice = adjustProds.reduce((sum, row) => sum + Number(row.price || 0), 0)
  const originalTotalPrice = adjustProds.reduce(
    (sum, row) => sum + Number(row.originalPrice || 0),
    0
  )
  const discountAmount =
    combinationPromotion.minDiscountAmount ??
    combinationPromotion.discountAmount ??
    Math.max(0, originalTotalPrice - comboTotalPrice)

  const activityId = normalized.activityId
  const activityCode = normalized.activityCode ?? normalized.promotionCode ?? ''
  const comboVO = {
    ...combinationPromotion,
    activityCode,
    promotionCode: activityCode ? String(activityCode).trim() : '',
    comboName: normalized.activityName || card.name || '',
    comboDesc: combinationPromotion.promotionDesc || normalized.promotionDesc || '',
    adjustProds,
    adjustProducts: adjustProds,
    candidateProds: adjustProds,
    comboTotalPrice,
    originalTotalPrice,
    discountAmount,
    minPurchaseText: defaultTier?.displayText || '',
    comboGroupKey: `combo:${activityId}`,
    activityStatus,
    phase: activityStatus === 'IN_PROGRESS' ? 1 : activityStatus === 'NOT_STARTED' ? 0 : 2,
    startTime,
    endTime,
    hasDuplicateSkuInCart: combinationPromotion.hasDuplicateSkuInCart === true,
  }

  const rows =
    adjustProds.length > 0
      ? adjustProds.map((item) => ({
          ...item,
          comboVO,
        }))
      : [{ comboVO }]

  return {
    key: `combo_landing:${activityId}`,
    kind: 'combo',
    isLanding: true,
    activityId,
    promotionId: activityId,
    activityCode: activityCode ? String(activityCode).trim() : '',
    promotionCode: activityCode ? String(activityCode).trim() : '',
    promotionType: PROMOTION_TYPE.COMBO_PROMO,
    inCart: combinationPromotion.hasDuplicateSkuInCart === true,
    cartBasketIds: [],
    tiers,
    rows,
    title: normalized.activityName || card.name || '',
    desc: normalized.promotionDesc || '',
    countdownLabel,
    targetTime,
    startTime,
    endTime,
    pendingActionText,
    activityStatus,
    phase: comboVO.phase,
  }
}
/**
 * @description 活动展示状态是否可透出（进行中 / 未开始）
 * @param {string} status
 * @returns {boolean}
 */
export function isPromotionDisplayStatusVisible(status) {
  return status === 'IN_PROGRESS' || status === 'NOT_STARTED'
}

/**
 * @description 活动是否进行中
 * @param {Object|null|undefined} detail
 * @returns {boolean}
 */
export function isPromotionDetailInProgress(detail) {
  const normalized = normalizePromotionDetailFields(detail)
  return normalized?.activityStatus === 'IN_PROGRESS'
}

/**
 * @description 解析活动起止时间戳
 * @param {Object|null|undefined} detail
 * @returns {{ startTime: number, endTime: number }|null}
 */
export function resolvePromotionDetailTimeRange(detail) {
  if (!detail || typeof detail !== 'object') return null
  const start = dateToTimestamp(detail.startTime)
  const end = dateToTimestamp(detail.endTime)
  if (!Number.isFinite(start) || !Number.isFinite(end) || start <= 0 || end <= 0) return null
  return { startTime: start, endTime: end }
}

/**
 * @description 从活动详情中定位当前商品行
 * @param {Object|null|undefined} detail
 * @param {string|number} prodId
 * @returns {Object|null}
 */
export function pickPromotionDetailProduct(detail, prodId) {
  if (!detail || typeof detail !== 'object') return null
  const numericProdId = Number(prodId)
  const products = Array.isArray(detail.products) ? detail.products : []
  if (Number.isFinite(numericProdId) && numericProdId > 0) {
    const matched = products.find((item) => Number(item?.prodId) === numericProdId)
    if (matched) return matched
  }
  return products[0] || null
}

/**
 * @description 活动详情 displayStatus 映射为旧买赠/折扣 status（1=未开始 2=进行中）
 * @param {string} displayStatus
 * @returns {number}
 */
function mapDisplayStatusToLegacyStatus(displayStatus) {
  if (displayStatus === 'IN_PROGRESS') return 2
  if (displayStatus === 'NOT_STARTED') return 1
  return 0
}

/**
 * @description 满量折扣详情映射为商详 discounts 列表项
 * @param {Object} detail
 * @param {Object|null} productRow
 * @returns {Array<Object>}
 */
function mapPromotionDetailToDiscounts(detail, productRow) {
  const tiers = Array.isArray(productRow?.tiers) ? productRow.tiers : []
  if (!tiers.length) return []

  return [
    {
      discountId: detail.promotionId,
      id: detail.promotionId,
      discountRule: 3,
      discountItems: tiers.map((tier) => ({
        needAmount: tier.buyNum,
        discount: tier.promotionDiscount,
        displayText: tier.displayText,
      })),
      startTime: detail.startTime,
      endTime: detail.endTime,
      status: mapDisplayStatusToLegacyStatus(detail.displayStatus),
      discountName: detail.promotionName,
    },
  ]
}

/**
 * @description 买赠详情映射为商详 giveaway 结构
 * @param {Object} detail
 * @param {Object|null} productRow
 * @returns {Object|null}
 */
function mapPromotionDetailToGiveaway(detail, productRow) {
  const tiers = Array.isArray(productRow?.tiers) ? productRow.tiers : []
  if (!tiers.length) return null

  const sortedTiers = sortBuyGiftTiers(tiers)
  const primaryTier = sortedTiers[0]
  const discountCalc = Number(detail.discountCalc)
  const isLoop = discountCalc === 2
  const buyNum = Number(primaryTier?.buyNum) || 1
  const giveawayRules = mapBuyGiftTiersToRuleGroups(sortedTiers, { discountCalc })
  const promoTags = buildDetailBuyGiftPromoTags({
    labels: productRow?.labels,
    tiers: sortedTiers,
    discountCalc,
    giveawayRules,
    buyNum,
    giftTotalValue: primaryTier?.giftTotalValue,
    displayText: primaryTier?.displayText,
    prodUnit: productRow?.prodUnit,
  })

  const giveawayProds = (Array.isArray(primaryTier?.gifts) ? primaryTier.gifts : []).map(
    (gift) => ({
      prodId: gift.prodId,
      prodName: gift.prodName,
      modelName: gift.prodModel,
      pic: gift.prodImage,
      giveawayNum: gift.giveawayNum,
      price: gift.unitPrice,
      oriPrice: gift.unitPrice,
      totalValue: gift.totalValue,
    })
  )

  const rulePrefix = isLoop ? '购买每满' : '购买满'
  const ruleDesc = `${rulePrefix}${buyNum}台即赠送，赠完为止`

  return {
    giveawayId: detail.promotionId,
    id: detail.promotionId,
    status: mapDisplayStatusToLegacyStatus(detail.displayStatus),
    startTime: detail.startTime,
    endTime: detail.endTime,
    buyNum,
    discountCalc,
    giveawayProds,
    giveawayRules,
    rules: giveawayRules,
    levels: giveawayRules,
    labels: Array.isArray(productRow?.labels) ? productRow.labels : [],
    promoTags,
    ruleDesc,
    tiers: sortedTiers,
    giftTotalValue: primaryTier?.giftTotalValue,
    giftTotalNum: primaryTier?.giftTotalNum,
    displayText: primaryTier?.displayText,
    prodUnit: productRow?.prodUnit || '',
  }
}

/**
 * @description 满量特价详情映射为商详 volumePriceActivity 结构
 * @param {Object} detail
 * @param {Object|null} productRow
 * @returns {Object|null}
 */
function mapPromotionDetailToVolumePriceActivity(detail, productRow) {
  if (!productRow) return null
  return {
    promotionId: detail.promotionId,
    promotionName: detail.promotionName,
    startTime: detail.startTime,
    endTime: detail.endTime,
    displayStatus: detail.displayStatus,
    labels: Array.isArray(productRow.labels) ? productRow.labels : [],
    tiers: Array.isArray(productRow.tiers) ? productRow.tiers : [],
    price: productRow.price || null,
    skus: Array.isArray(productRow.skus) ? productRow.skus : [],
  }
}

/**
 * @description 解析搭配套餐主商品与搭配列表（兼容 mainProduct/comboProducts、mainProd/matchingProds、itemList）
 * @param {Object|null|undefined} combo
 * @returns {{ main: Object|null, matching: Object[] }}
 */
export function resolveComboBundleProducts(combo) {
  if (!combo || typeof combo !== 'object') {
    return { main: null, matching: [] }
  }

  if (combo.mainProduct && typeof combo.mainProduct === 'object') {
    return {
      main: combo.mainProduct,
      matching: Array.isArray(combo.comboProducts) ? combo.comboProducts : [],
    }
  }

  if (combo.mainProd && typeof combo.mainProd === 'object') {
    return {
      main: combo.mainProd,
      matching: Array.isArray(combo.matchingProds)
        ? combo.matchingProds
        : Array.isArray(combo.comboProducts)
          ? combo.comboProducts
          : [],
    }
  }

  const itemList = Array.isArray(combo.itemList) ? combo.itemList : []
  if (itemList.length === 0) {
    return { main: null, matching: [] }
  }

  const mainIndex = itemList.findIndex(
    (item) =>
      item?.mainProd === true ||
      item?.isMain === true ||
      Number(item?.comboItemType) === 1 ||
      Number(item?.mainProduct) === 1
  )
  const resolvedMainIndex = mainIndex >= 0 ? mainIndex : 0
  return {
    main: itemList[resolvedMainIndex],
    matching: itemList.filter((_, index) => index !== resolvedMainIndex),
  }
}

/**
 * @description 搭配套餐商品起购件数（接口 `startNum`）
 * @param {Object|null|undefined} item
 * @returns {number}
 */
export function resolveComboBundleItemStartNum(item) {
  if (!item || typeof item !== 'object') return 1
  const n = Math.trunc(Number(item.startNum ?? item.leastNum ?? 1))
  return Number.isFinite(n) && n >= 1 ? n : 1
}

/**
 * @description 搭配套餐商品限购件数（接口 `maxNum`，无则 undefined）
 * @param {Object|null|undefined} item
 * @returns {number|undefined}
 */
export function resolveComboBundleItemMaxNum(item) {
  if (!item || typeof item !== 'object') return undefined
  const raw = item.maxNum
  if (raw === null || raw === undefined || raw === '') return undefined
  const n = Math.trunc(Number(raw))
  return Number.isFinite(n) && n >= 1 ? n : undefined
}

/**
 * @description 为搭配套餐商品写入 startNum/leastNum/maxNum（起购读 startNum，限购读 maxNum）
 * @param {Object|null|undefined} item
 * @returns {Object|null|undefined}
 */
export function normalizeComboBundleItemQty(item) {
  if (!item || typeof item !== 'object') return item
  const startNum = resolveComboBundleItemStartNum(item)
  const maxNum = resolveComboBundleItemMaxNum(item)
  return {
    ...item,
    startNum,
    leastNum: startNum,
    ...(maxNum !== undefined ? { maxNum } : {}),
  }
}

/**
 * @description 归一化搭配套餐 comboVO（购物车/商详共用，统一 mainProduct + comboProducts 数量字段）
 * @param {Object|null|undefined} comboVO
 * @returns {Object|null|undefined}
 */
export function normalizeComboBundleVo(comboVO) {
  if (!comboVO || typeof comboVO !== 'object') return comboVO

  const container = comboVO.combo && typeof comboVO.combo === 'object' ? comboVO.combo : comboVO
  const { main, matching } = resolveComboBundleProducts(container)
  if (!main || typeof main !== 'object') return comboVO

  const mainNormalized = normalizeComboBundleItemQty(main)
  const matchingSource = Array.isArray(container.comboProducts)
    ? container.comboProducts
    : Array.isArray(container.matchingProds)
      ? container.matchingProds
      : matching
  const matchingNormalized = matchingSource.map((item) => normalizeComboBundleItemQty(item))

  return {
    ...comboVO,
    mainProd: normalizeComboBundleItemQty(comboVO.mainProd ?? mainNormalized),
    mainProduct: mainNormalized,
    matchingProds: matchingNormalized,
    comboProducts: matchingNormalized,
  }
}

/**
 * @description 搭配套餐 SKU/商品行起购限购字段（仅 startNum + maxNum）
 * @param {Object|null|undefined} item
 * @param {Object|null|undefined} [sku]
 * @returns {{ startNum: number, leastNum: number, maxNum?: number }}
 */
function mapComboBundleProdQtyFields(item, sku = null) {
  const normalized = normalizeComboBundleItemQty({
    startNum: sku?.startNum ?? item?.startNum,
    maxNum: sku?.maxNum ?? item?.maxNum,
  })
  return {
    startNum: normalized.startNum,
    leastNum: normalized.leastNum,
    ...(normalized.maxNum !== undefined ? { maxNum: normalized.maxNum } : {}),
  }
}

/**
 * @description 搭配套餐详情映射为 DetailComboPackage 可消费的 combo 结构
 * @param {Object} detail
 * @returns {Object|null}
 */
export function mapPromotionDetailToComboDetail(detail) {
  const normalized = normalizePromotionDetailFields(detail)
  const combo = normalized?.combo
  if (!combo || typeof combo !== 'object') return null

  const { main, matching } = resolveComboBundleProducts(combo)
  if (!main || typeof main !== 'object') return null

  const mapComboProd = (item) => {
    const skus = Array.isArray(item.skus) ? item.skus : []
    const defaultSku = skus[0]
    const defaultSkuId = item.skuId ?? defaultSku?.skuId ?? item.defaultSkuId
    const prodQty = mapComboBundleProdQtyFields(item, defaultSku)
    const catalogPrice = pickPromotionCatalogUnitPrice(item)

    return {
      prodId: item.prodId,
      prodName: item.prodName,
      modelName: item.prodModel,
      pic: item.prodImage,
      prodPic: item.prodImage,
      prodUnit: item.prodUnit,
      comboPrice: item.comboPrice,
      price: catalogPrice ?? item.originalPrice,
      originalPrice: catalogPrice ?? item.originalPrice,
      skuId: defaultSkuId,
      defaultSkuId,
      skuList: skus.map((sku) => {
        const skuQty = mapComboBundleProdQtyFields(item, sku)
        const skuCatalogPrice = pickPromotionCatalogUnitPrice(sku)
        return {
          skuId: sku.skuId,
          skuName: sku.specText,
          pic: sku.skuImage ?? item.prodImage,
          comboPrice: sku.promotionPrice,
          price: skuCatalogPrice ?? sku.originalPrice,
          originalPrice: skuCatalogPrice ?? sku.originalPrice,
          ...skuQty,
        }
      }),
      ...prodQty,
      required: item.required,
      briefDetail: item.prodDesc,
      isSelect: item.required === false ? 0 : 1,
    }
  }

  const bundleVo = normalizeComboBundleVo({
    mainProd: mapComboProd(main),
    mainProduct: mapComboProd(main),
    matchingProds: matching.map((item) => mapComboProd(item)),
    comboProducts: matching.map((item) => mapComboProd(item)),
  })

  return {
    comboId: normalized.activityId,
    promotionId: normalized.activityId,
    promotionCode: normalized.activityCode,
    promotionName: normalized.activityName,
    startTime: normalized.startTime,
    endTime: normalized.endTime,
    displayStatus: normalized.activityStatus,
    activityStatus: normalized.activityStatus === 'IN_PROGRESS' ? 2 : 1,
    mainProdId: main.prodId,
    ...bundleVo,
    comboTotalPrice: combo.comboTotalPrice,
    originalTotalPrice: combo.originalTotalPrice,
    discountAmount: combo.minDiscountAmount ?? combo.discountAmount,
  }
}

/**
 * @description 解析商详应使用的活动 ID（路由 query 优先，其次详情 VO）
 * @param {Object} [options]
 * @param {string|number} [options.routePromotionId]
 * @param {Object} [options.productSnapshot]
 * @returns {number|null}
 */
export function resolvePromotionDetailId({ routePromotionId, productSnapshot } = {}) {
  const routeRaw = routePromotionId
  if (routeRaw !== undefined && routeRaw !== null && String(routeRaw).trim() !== '') {
    const routeNum = Number(routeRaw)
    if (Number.isFinite(routeNum) && routeNum > 0) return routeNum
  }

  const snapshotRaw = productSnapshot?.promotionId ?? productSnapshot?.promotionCode
  if (snapshotRaw !== undefined && snapshotRaw !== null && String(snapshotRaw).trim() !== '') {
    const snapshotNum = Number(snapshotRaw)
    if (Number.isFinite(snapshotNum) && snapshotNum > 0) return snapshotNum
  }

  return null
}

/**
 * @description 解析活动详情请求模式
 * @param {Object} [options]
 * @param {string|number} [options.routePromotionId]
 * @param {Object} [options.productSnapshot]
 * @returns {{ mode: 'by-id', promotionId: number }|{ mode: 'by-product', productId: number }}
 */
export function resolvePromotionDetailRequest({
  routePromotionId,
  productSnapshot,
  productId,
} = {}) {
  const promotionId = resolvePromotionDetailId({ routePromotionId, productSnapshot })
  if (promotionId) {
    return { mode: 'by-id', promotionId }
  }

  const normalizedProductId = normalizeStoreProdId(productId)
  if (normalizedProductId) {
    return { mode: 'by-product', productId: normalizedProductId }
  }

  return null
}

/**
 * @description 活动详情映射为商详 productInfo 增量字段
 * @param {Object} detail - 活动详情
 * @param {string|number} prodId - 当前商品 ID
 * @returns {Object}
 */
export function mapPromotionDetailToProductPatch(detail, prodId) {
  const normalized = normalizePromotionDetailFields(detail)
  if (!normalized) return {}

  const promotionType = Number(normalized.activityType)
  const productRow = pickPromotionDetailProduct(normalized, prodId)
  const patch = {
    promotionDetail: normalized,
    promotionType,
    promotionId: normalized.activityId,
    promotionCode: normalized.activityCode,
    promotionName: normalized.activityName,
    promotionDisplayStatus: normalized.activityStatus,
    activityProductImage: resolvePromotionDetailProductImage(normalized, prodId),
  }

  if (promotionType === PROMOTION_TYPE.VOLUME_DISCOUNT) {
    patch.discounts = mapPromotionDetailToDiscounts(normalized, productRow)
  }

  if (promotionType === PROMOTION_TYPE.BUY_GIFT) {
    const giveaway = mapPromotionDetailToGiveaway(normalized, productRow)
    if (giveaway) patch.giveaway = giveaway
  }

  if (promotionType === PROMOTION_TYPE.VOLUME_PRICE) {
    const volumePriceActivity = mapPromotionDetailToVolumePriceActivity(normalized, productRow)
    if (volumePriceActivity) patch.volumePriceActivity = volumePriceActivity
  }

  if (promotionType === PROMOTION_TYPE.COMBO_BUNDLE) {
    const comboDetail = mapPromotionDetailToComboDetail(normalized)
    if (comboDetail) patch.promotionComboDetail = comboDetail
  }

  if (promotionType === PROMOTION_TYPE.COMBO_PROMO) {
    patch.promotionCombination = normalized.combinationPromotion || null
  }

  return patch
}

/**
 * @description 商详买赠/特价/折扣是否仅展示价目表价（不展示活动价与划线删除价）
 * @param {Object|null|undefined} detail
 * @returns {boolean}
 */
export function isPromotionDetailCatalogPriceType(detail) {
  if (!detail || !isPromotionDetailInProgress(detail)) return false
  const pt = Number(detail.promotionType ?? detail.activityType)
  return (
    pt === PROMOTION_TYPE.VOLUME_PRICE ||
    pt === PROMOTION_TYPE.VOLUME_DISCOUNT ||
    pt === PROMOTION_TYPE.BUY_GIFT
  )
}

/**
 * @description 商详活动商品价目表单价（身份价，无则终端客户价；不含活动价）
 * @param {Object|null|undefined} productInfo
 * @param {Object|null} [currentSku]
 * @returns {number|null}
 */
export function resolvePromotionDetailCatalogPrice(productInfo, currentSku = null) {
  const detail = productInfo?.promotionDetail
  if (!detail || !isPromotionDetailCatalogPriceType(detail)) return null

  const productRow = pickPromotionDetailProduct(detail, productInfo?.prodId)
  if (productRow) {
    const skuId = currentSku?.skuId ?? currentSku?.id
    if (skuId != null && Array.isArray(productRow.skus)) {
      const promoSku = productRow.skus.find((item) => Number(item?.skuId) === Number(skuId))
      if (promoSku) {
        const fromSku = pickPromotionCatalogUnitPrice(promoSku)
        if (fromSku !== null && fromSku > 0) return fromSku
      }
    }

    const priceInfo =
      productRow.price && typeof productRow.price === 'object' ? productRow.price : {}
    const fromPriceInfo = pickPromotionCatalogUnitPrice(priceInfo)
    if (fromPriceInfo !== null && fromPriceInfo > 0) return fromPriceInfo

    const fromRow = pickPromotionCatalogUnitPrice(productRow)
    if (fromRow !== null && fromRow > 0) return fromRow
  }

  if (currentSku) {
    const skuPrice = Number(currentSku.price)
    if (Number.isFinite(skuPrice) && skuPrice > 0) return skuPrice
  }

  const singlePrice = Number(productInfo?.price)
  if (Number.isFinite(singlePrice) && singlePrice > 0) return singlePrice

  return null
}

/**
 * @description 从活动详情解析当前商品活动价（仅进行中生效）
 * @param {Object|null|undefined} productInfo
 * @param {Object|null} [currentSku]
 * @returns {{ price: number, originalPrice: number|null }|null}
 */
export function getPromotionDetailPriceInfo(productInfo, currentSku = null) {
  const detail = productInfo?.promotionDetail
  if (!detail || !isPromotionDetailInProgress(detail)) return null

  const pt = Number(detail.promotionType)
  if (
    pt !== PROMOTION_TYPE.VOLUME_PRICE &&
    pt !== PROMOTION_TYPE.VOLUME_DISCOUNT &&
    pt !== PROMOTION_TYPE.BUY_GIFT
  ) {
    return null
  }

  const productRow = pickPromotionDetailProduct(detail, productInfo?.prodId)
  if (!productRow) return null

  const skuId = currentSku?.skuId ?? currentSku?.id
  if (skuId != null && Array.isArray(productRow.skus)) {
    const promoSku = productRow.skus.find((item) => Number(item?.skuId) === Number(skuId))
    if (promoSku && Number(promoSku.promotionPrice) > 0) {
      const original = Number(promoSku.originalPrice ?? productRow.price?.originalPrice)
      return {
        price: Number(promoSku.promotionPrice),
        originalPrice: Number.isFinite(original) && original > 0 ? original : null,
      }
    }
  }

  const displayPrice = Number(productRow.price?.displayPrice)
  const originalPrice = Number(productRow.price?.originalPrice)
  if (displayPrice > 0) {
    return {
      price: displayPrice,
      originalPrice: Number.isFinite(originalPrice) && originalPrice > 0 ? originalPrice : null,
    }
  }

  if (pt === PROMOTION_TYPE.BUY_GIFT) {
    return {
      price: 0,
      originalPrice: Number.isFinite(originalPrice) && originalPrice > 0 ? originalPrice : null,
    }
  }

  return null
}

/**
 * @description 活动详情是否应在商详展示（含未开始）
 * @param {Object|null|undefined} detail
 * @returns {boolean}
 */
export function isPromotionDetailDisplayable(detail) {
  const normalized = normalizePromotionDetailFields(detail)
  if (!normalized) return false
  return isPromotionDisplayStatusVisible(normalized.activityStatus)
}
